import React, { useCallback, useMemo, useState } from 'react';
import dynamic from 'next/dynamic';

import { Button, Column, Row, WebpImageWithFallback } from '@husslehq/ui-library';

// Load carousel gallery dynamically
import { ImageCarouselGalleryProps } from '../../components/ImageCarouselGallery/ImageCarouselGallery';
const ImageCarouselGallery = dynamic<ImageCarouselGalleryProps>(() =>
  import('../../components/ImageCarouselGallery/ImageCarouselGallery').then((mod) => mod.ImageCarouselGallery),
);

import { track } from '../../services/analytics';

import styles from '../../components/Mosaic/Mosaic.module.scss';

export interface IMosaicImage {
  url: string;
  alt: string;
}

export interface IGalleryImage {
  original: string;
  thumbnail: string;
}

type Props = {
  data: IMosaicImage[];
  firstImage: string;
  mobileFirstImage: string;
  galleryData: IGalleryImage[];
  gymName: string;
};

export const Mosaic: React.FC<Props> = ({ data, firstImage, mobileFirstImage, galleryData, gymName }) => {
  const assetPrefix = process.env.NEXT_PUBLIC_ASSET_PREFIX ?? '';
  const numberOfImages = 5;
  const [isShowModal, setIsShowModal] = useState(false);
  const [startIndex, setStartIndex] = useState(0);

  const handleViewGalleryClick = (idx: number) => () => {
    document.body.classList.add('prevent-body-scrolling');
    setStartIndex(idx);
    setIsShowModal(true);
    track({
      event: 'gym_spa',
      category: 'Gym Details',
      action: 'Views Photos',
      label: 'all_photos',
    });
  };

  const handleCloseModal = useCallback(() => {
    document.body.classList.remove('prevent-body-scrolling');
    setIsShowModal(false);
    track({
      event: 'gym_spa',
      category: 'Gym Details',
      action: 'Views Photos',
      label: 'back_to_gym_details',
    });
  }, []);

  const mosaicList = useMemo(
    () =>
      data.slice(1, numberOfImages).map((image, idx) => (
        <Column key={`mosaic_item_${idx}`} className={styles['mosaic__grid']} sm="6">
          <div
            className={`${styles['mosaic__item']} ${styles['mosaic__item--clickable']}`}
            onClick={handleViewGalleryClick(idx + 1)}
          >
            <WebpImageWithFallback
              width="465"
              height="335"
              alt={'Photo of ' + gymName}
              className={styles['mosaic__image']}
              src={image.url}
            />
          </div>
        </Column>
      )),
    [data],
  );

  const emptyImages = useMemo(
    () =>
      Array.from({ length: numberOfImages - data.length }, (i) => (
        <Column key={`mosaic_item_empty${i}`} className={styles['mosaic__grid']} smDisplay="block" sm="6">
          <div className={styles['mosaic__item']}>
            <img
              className={styles['mosaic__image']}
              alt="Placeholder illustration of 2 polaroid images"
              src={assetPrefix + '/img/image-placeholder.jpg'}
              loading="lazy"
            />
          </div>
        </Column>
      )),
    [data],
  );

  return (
    <div className={styles.mosaic}>
      <Button
        className={styles['mosaic__gallery-btn']}
        size="medium"
        level="outline"
        onClick={handleViewGalleryClick(0)}
      >
        All photos
      </Button>
      <Row>
        <Column md="6">
          <div
            key={`mosaic_item_first`}
            className={`${styles['mosaic__item']} ${styles['mosaic__item--first']} ${styles['mosaic__item--clickable']}`}
            onClick={handleViewGalleryClick(0)}
          >
            <WebpImageWithFallback
              alt={'Photo of ' + gymName}
              className={styles['mosaic__image']}
              src={firstImage}
              mobileSrc={mobileFirstImage}
              loading="eager"
              width="385"
              height="265"
            />
          </div>
        </Column>
        <Column xsDisplay="none" mdDisplay="block" md="6">
          <Row>
            {mosaicList}
            {data.length < numberOfImages && emptyImages}
          </Row>
        </Column>
        <ImageCarouselGallery
          galleryData={galleryData}
          onClose={handleCloseModal}
          modalVisible={isShowModal}
          startIndex={startIndex}
        />
        {/* preload first 5 images in gallery */}
        <div className={styles['mosaic__preloaded']}>
          {galleryData.slice(0, numberOfImages).map((image, idx) => (
            <div key={`prerender_item_${idx}`}>
              <WebpImageWithFallback alt="gym image" className={styles['mosaic__image']} src={image.original} />
            </div>
          ))}
        </div>
      </Row>
    </div>
  );
};

export { Mosaic as default };
